import { useState } from 'react';
import {
  useClockodoConfig,
  useUpdateClockodoConfig,
  useServiceMappings,
  useCreateServiceMapping,
  useDeleteServiceMapping,
  useClockodoServices,
  useClockodoSyncHealth,
  useCpcActiveFactorSet,
} from './cpc-hooks';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '0.875rem',
};

const btnStyle = (variant: 'primary' | 'danger' | 'secondary' = 'primary'): React.CSSProperties => ({
  padding: '0.4rem 1rem',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.875rem',
  background:
    variant === 'danger' ? 'var(--color-danger, #dc3545)' :
    variant === 'secondary' ? 'var(--color-border)' :
    'var(--color-primary)',
  color: variant === 'secondary' ? 'var(--color-text)' : '#fff',
});

export function ClockodoAdminTab() {
  const [section, setSection] = useState<'config' | 'mappings'>('config');

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button style={btnStyle(section === 'config' ? 'primary' : 'secondary')} onClick={() => setSection('config')}>
          Konfiguration
        </button>
        <button style={btnStyle(section === 'mappings' ? 'primary' : 'secondary')} onClick={() => setSection('mappings')}>
          Leistungs-Mapping
        </button>
      </div>

      {section === 'config' ? <ClockodoConfigSection /> : <ServiceMappingSection />}
    </div>
  );
}

function ClockodoConfigSection() {
  const { data: config, isLoading } = useClockodoConfig();
  const { data: health } = useClockodoSyncHealth();
  const updateConfig = useUpdateClockodoConfig();

  const [email, setEmail] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [interval, setInterval] = useState('');
  const [ptToHours, setPtToHours] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  const handleEdit = () => {
    setEmail(config?.clockodoEmail ?? '');
    setApiKey(''); // never pre-fill API key
    setInterval(String(config?.syncIntervalMin ?? 15));
    setPtToHours(String(config?.ptToHours ?? '8.00'));
    setEditing(true);
    setError('');
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload: Record<string, any> = {};
      if (email) payload.clockodoEmail = email;
      if (apiKey) payload.clockodoApiKey = apiKey;
      const intVal = parseInt(interval, 10);
      if (!isNaN(intVal) && intVal >= 5) payload.syncIntervalMin = intVal;
      const ptVal = parseFloat(ptToHours);
      if (!isNaN(ptVal) && ptVal >= 1) payload.ptToHours = ptVal;
      await updateConfig.mutateAsync(payload);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  if (isLoading) return <p>Lädt...</p>;

  return (
    <div style={{ maxWidth: 600 }}>
      <h4 style={{ marginTop: 0 }}>Sync-Health</h4>
      {health ? (
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          <span>Gesamt: <strong>{health.total}</strong></span>
          <span style={{ color: 'var(--color-success, #198754)' }}>Verknüpft: <strong>{health.linked}</strong></span>
          <span style={{ color: 'var(--color-danger, #dc3545)' }}>Fehler: <strong>{health.syncError}</strong></span>
          <span>Nicht verknüpft: <strong>{health.notLinked}</strong></span>
          {health.lastSyncedAt && (
            <span style={{ color: 'var(--color-text-muted)' }}>
              Letzter Sync: {new Date(health.lastSyncedAt).toLocaleString('de-DE')}
            </span>
          )}
        </div>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Sync-Health nicht verfügbar</p>
      )}

      <h4>Clockodo API-Zugangsdaten</h4>
      {!editing ? (
        <div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}>
            <tbody>
              <tr>
                <td style={{ padding: '0.4rem 0.8rem 0.4rem 0', color: 'var(--color-text-muted)', width: 160 }}>E-Mail</td>
                <td style={{ padding: '0.4rem 0' }}>{config?.clockodoEmail || <em style={{ color: 'var(--color-text-muted)' }}>nicht konfiguriert</em>}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.4rem 0.8rem 0.4rem 0', color: 'var(--color-text-muted)' }}>API-Key</td>
                <td style={{ padding: '0.4rem 0' }}>{config?.clockodoApiKey ? '••••••••••••' : <em style={{ color: 'var(--color-text-muted)' }}>nicht gesetzt</em>}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.4rem 0.8rem 0.4rem 0', color: 'var(--color-text-muted)' }}>Sync-Intervall</td>
                <td style={{ padding: '0.4rem 0' }}>{config?.syncIntervalMin ?? 15} Minuten</td>
              </tr>
              <tr>
                <td style={{ padding: '0.4rem 0.8rem 0.4rem 0', color: 'var(--color-text-muted)' }}>Stunden pro PT</td>
                <td style={{ padding: '0.4rem 0' }}>{config?.ptToHours ?? '8.00'} h</td>
              </tr>
            </tbody>
          </table>
          <button style={{ ...btnStyle('primary'), marginTop: '1rem' }} onClick={handleEdit}>
            Bearbeiten
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Clockodo E-Mail</span>
            <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </label>
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>API-Key (leer lassen = unverändert)</span>
            <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={apiKey} onChange={(e) => setApiKey(e.target.value)} type="password" placeholder="API-Key eingeben..." />
          </label>
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Sync-Intervall (Minuten, min. 5)</span>
            <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={interval} onChange={(e) => setInterval(e.target.value)} type="number" min={5} />
          </label>
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Stunden pro Personentag</span>
            <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={ptToHours} onChange={(e) => setPtToHours(e.target.value)} type="number" min={1} step={0.5} />
          </label>
          {error && <p style={{ color: 'var(--color-danger, #dc3545)', fontSize: '0.875rem', margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={btnStyle('primary')} onClick={handleSave} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? 'Speichert...' : 'Speichern'}
            </button>
            <button style={btnStyle('secondary')} onClick={() => setEditing(false)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceMappingSection() {
  const { data: mappings = [], isLoading } = useServiceMappings();
  const { data: services = [] } = useClockodoServices();
  const { data: activeFactorSet } = useCpcActiveFactorSet();
  const createMapping = useCreateServiceMapping();
  const deleteMapping = useDeleteServiceMapping();

  const roles: string[] = activeFactorSet?.payload?.roles?.map((r) => r.key) ?? [];

  const [showForm, setShowForm] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');

  const mappedServiceIds = new Set(mappings.map((m) => m.clockodoServiceId));
  const availableServices = services.filter((s) => !mappedServiceIds.has(s.id));

  const handleServiceChange = (id: string) => {
    setServiceId(id);
    const svc = services.find((s) => String(s.id) === id);
    if (svc) setServiceName(svc.name);
  };

  const handleCreate = async () => {
    setError('');
    const idNum = parseInt(serviceId, 10);
    if (isNaN(idNum)) { setError('Bitte eine Clockodo-Leistung wählen'); return; }
    if (!role) { setError('Bitte eine Rolle wählen'); return; }
    try {
      await createMapping.mutateAsync({ clockodoServiceId: idNum, clockodoServiceName: serviceName || undefined, role });
      setShowForm(false);
      setServiceId('');
      setServiceName('');
      setRole('');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  if (isLoading) return <p>Lädt...</p>;

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: 0 }}>
        Ordne Clockodo-Leistungen den CPC-Rollen zu. Zeiteinträge mit nicht gemappten Leistungen werden beim Sync ignoriert.
      </p>

      {mappings.length === 0 ? (
        <p style={{ fontSize: '0.875rem', fontStyle: 'italic', color: 'var(--color-text-muted)' }}>Noch keine Mappings vorhanden.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginBottom: '1rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.75rem' }}>Clockodo-Service-ID</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Name</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>CPC-Rolle</th>
              <th style={{ padding: '0.5rem 0.75rem' }}></th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => (
              <tr key={m.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 0.75rem' }}>{m.clockodoServiceId}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{m.clockodoServiceName ?? '—'}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}><code>{m.role}</code></td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                  <button style={btnStyle('danger')} onClick={() => deleteMapping.mutate(m.id)}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!showForm ? (
        <button style={btnStyle('primary')} onClick={() => { setShowForm(true); setError(''); }}>
          + Mapping hinzufügen
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: 480, marginTop: '1rem' }}>
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Clockodo-Leistung</span>
            {availableServices.length > 0 ? (
              <select style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={serviceId} onChange={(e) => handleServiceChange(e.target.value)}>
                <option value="">Bitte wählen…</option>
                {availableServices.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.name} (ID: {s.id})</option>
                ))}
              </select>
            ) : (
              <input
                style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }}
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                placeholder="Clockodo-Service-ID (Zahl)"
                type="number"
              />
            )}
          </label>
          {!availableServices.length && (
            <label>
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Leistungsname (optional)</span>
              <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={serviceName} onChange={(e) => setServiceName(e.target.value)} />
            </label>
          )}
          <label>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>CPC-Rolle</span>
            {roles.length > 0 ? (
              <select style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">Bitte wählen…</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : (
              <input style={{ ...inputStyle, display: 'block', marginTop: '0.25rem' }} value={role} onChange={(e) => setRole(e.target.value)} placeholder="Rollen-Key" />
            )}
          </label>
          {error && <p style={{ color: 'var(--color-danger, #dc3545)', fontSize: '0.875rem', margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={btnStyle('primary')} onClick={handleCreate} disabled={createMapping.isPending}>
              {createMapping.isPending ? 'Speichert...' : 'Anlegen'}
            </button>
            <button style={btnStyle('secondary')} onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}
